import { useState } from 'react';
import { SECTION_COPY, SectionEnum, type Section } from '@flashpoint/site-dna';
import type { StepProps } from '../stepProps';

export function SectionsStep({ lead, queueField }: StepProps) {
  const order: Section[] = lead.siteDna.layout.sectionOrder;
  const inUse = new Set(order);
  const available: Section[] = SectionEnum.options.filter((s) => !inUse.has(s));
  const [pitchOpen, setPitchOpen] = useState<Section | null>(null);

  const setOrder = (next: Section[]) => queueField('siteDna.layout.sectionOrder', next);

  const move = (i: number, dir: -1 | 1) => {
    const target = i + dir;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[i], next[target]] = [next[target]!, next[i]!];
    setOrder(next);
  };

  const remove = (i: number) => setOrder(order.filter((_, idx) => idx !== i));
  const add = (s: Section) => setOrder([...order, s]);

  return (
    <div className="space-y-5">
      <p className="text-xs text-nm-muted">
        Reorder, remove, or add sections. Click the <span className="font-mono">?</span> on any
        section to read the pitch aloud to a client deciding whether to include it.
      </p>

      <div>
        <div className="mb-2 text-[11px] uppercase tracking-widest text-nm-muted">
          Sections in order
        </div>
        <ul className="space-y-2">
          {order.map((s, i) => {
            const copy = SECTION_COPY[s];
            return (
              <li
                key={`${s}-${i}`}
                className="rounded-lg border border-nm-border bg-nm-moss/30"
              >
                <div className="flex items-center gap-2 p-2">
                  <span className="font-mono text-[10px] text-nm-muted">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1">
                    <div className="font-semibold text-nm-ink">{copy.name}</div>
                    <div className="text-xs text-nm-muted">{copy.oneLiner}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPitchOpen(pitchOpen === s ? null : s)}
                    className="rounded border border-nm-border px-2 py-1 text-xs text-nm-ink hover:border-nm-accent"
                    aria-label="Read pitch"
                    aria-expanded={pitchOpen === s}
                  >
                    ?
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="rounded border border-nm-border px-2 py-1 text-xs text-nm-ink hover:border-nm-accent disabled:opacity-30"
                    aria-label="Move up"
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === order.length - 1}
                    className="rounded border border-nm-border px-2 py-1 text-xs text-nm-ink hover:border-nm-accent disabled:opacity-30"
                    aria-label="Move down"
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="rounded px-2 py-1 text-xs text-nm-danger hover:opacity-80"
                  >
                    Remove
                  </button>
                </div>
                {pitchOpen === s && <SectionPitch section={s} />}
              </li>
            );
          })}
        </ul>
      </div>

      {available.length > 0 && (
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-widest text-nm-muted">
            Available sections
          </div>
          <div className="space-y-2">
            {available.map((s) => {
              const copy = SECTION_COPY[s];
              return (
                <div
                  key={s}
                  className="rounded-lg border border-dashed border-nm-border bg-nm-moss/15"
                >
                  <div className="flex items-center gap-2 p-2">
                    <div className="flex-1">
                      <div className="font-semibold text-nm-ink">{copy.name}</div>
                      <div className="text-xs text-nm-muted">{copy.oneLiner}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPitchOpen(pitchOpen === s ? null : s)}
                      className="rounded border border-nm-border px-2 py-1 text-xs text-nm-ink hover:border-nm-accent"
                      aria-label="Read pitch"
                      aria-expanded={pitchOpen === s}
                    >
                      ?
                    </button>
                    <button
                      type="button"
                      onClick={() => add(s)}
                      className="rounded-md border border-nm-border px-3 py-1.5 text-sm text-nm-ink hover:border-nm-accent"
                    >
                      + Add
                    </button>
                  </div>
                  {pitchOpen === s && <SectionPitch section={s} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionPitch({ section }: { section: Section }) {
  const copy = SECTION_COPY[section];
  return (
    <div className="space-y-3 border-t border-nm-border px-3 py-3">
      <p className="border-l-2 border-flashpoint-ember/60 pl-3 text-sm italic text-nm-ink">
        {copy.clientPitch}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-nm-muted">
          Best for
        </span>
        {copy.bestFor.map((p) => (
          <span
            key={p}
            className="rounded-full bg-nm-chrome px-2 py-0.5 text-[11px] text-nm-ink"
          >
            {p}
          </span>
        ))}
      </div>
      {copy.avoidIf.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-nm-danger/70">
            Avoid if
          </span>
          {copy.avoidIf.map((p) => (
            <span
              key={p}
              className="rounded-full bg-nm-danger/15 px-2 py-0.5 text-[11px] text-nm-danger"
            >
              {p}
            </span>
          ))}
        </div>
      )}
      {copy.feel.length > 0 && (
        <div className="font-mono text-[11px] uppercase tracking-widest text-nm-muted">
          feel · {copy.feel.join(' · ')}
        </div>
      )}
    </div>
  );
}
